import type { FlagGuardrail, FlagGuardrailArm, FlagStepGate } from 'definitions';
import type { FlagRolloutSelect } from 'types';
import { FLAG_GUARDRAIL_DEFAULT_MARGIN_BP } from 'definitions';

/*
 * What the flag page shows of the guardrail (PER-340). Crons Experiment Stats writes the last
 * check of the current step into flag_rollouts.guardrail.check; the scheduler advances a 'kpi'
 * step only on a 'clear' or 'pass' check of that very step (decide-flag-action.ts). Pure, so
 * the card says exactly what the scheduler will act on.
 */

export type GuardrailCheck = NonNullable<FlagGuardrail['check']>;

type GuardrailRollout = Pick<FlagRolloutSelect, 'currentStep' | 'stepStartedAt'> &
  Partial<Pick<FlagRolloutSelect, 'guardrail'>>;

/** The current step's gate, or null when the plan has no step there. */
export const currentGate = (rollout: Pick<FlagRolloutSelect, 'steps' | 'currentStep'>): FlagStepGate | null =>
  rollout.steps[rollout.currentStep]?.gate ?? null;

/**
 * The stored check when it is about the step the plan is on now, else null. A check of an
 * earlier step, or of this step before a resume restarted it, says nothing about this one, and
 * the scheduler ignores it the same way.
 */
export function checkOfCurrentStep(rollout: GuardrailRollout): GuardrailCheck | null {
  const check = rollout.guardrail?.check;
  if (!check) return null;
  return check.step === rollout.currentStep && check.stepStartedAt === rollout.stepStartedAt ? check : null;
}

/** The margin the guardrail judges with, in basis points. */
export const guardrailMarginBp = (rollout: Pick<FlagRolloutSelect, 'guardrail'>) =>
  rollout.guardrail?.marginBp ?? FLAG_GUARDRAIL_DEFAULT_MARGIN_BP;

/**
 * Whether the plan was paused by a guardrail breach rather than by a person: paused, with a
 * breach check of the step it is on. Read from the stored check, not the pause reason's wording.
 * A resume restarts the step, so its old breach stops counting; a new margin drops the check.
 */
export const isGuardrailPause = (rollout: GuardrailRollout & Pick<FlagRolloutSelect, 'planStatus'>) =>
  rollout.planStatus === 'paused' && checkOfCurrentStep(rollout)?.verdict === 'breach';

/** A side's conversion rate as "4.82%", or null with no visitors. */
export function conversionRate(arm: FlagGuardrailArm): string | null {
  if (arm.visitors <= 0) return null;
  return `${((arm.conversions / arm.visitors) * 100).toFixed(2)}%`;
}

/** A relative change as "+1.2%" or "−3.1%" (a minus sign, not a dash). */
export function signedPercent(ratio: number): string {
  const value = Number((ratio * 100).toFixed(1));
  if (value === 0) return '0.0%';
  return `${value > 0 ? '+' : '−'}${Math.abs(value).toFixed(1)}%`;
}

/** The always-valid interval for On against Off: "−3.1% to +1.2%". */
export const relativeInterval = ({ lower, upper }: { lower: number; upper: number }) =>
  `${signedPercent(lower)} to ${signedPercent(upper)}`;

/** A count as people read it: 12,480. */
export const formatCount = (value: number) => value.toLocaleString('en-US');

type ShowsGuardrailParams = {
  rollout: Pick<FlagRolloutSelect, 'steps' | 'currentStep' | 'planStatus'>;
  isArchived: boolean;
};

/**
 * Whether the flag page shows the Guardrail card: while the step the plan is on (or starts on,
 * for a draft) waits on the guardrail. Not once the plan is done or the flag archived: nothing
 * checks it then.
 */
export function showsGuardrail({ rollout, isArchived }: ShowsGuardrailParams): boolean {
  if (isArchived || rollout.planStatus === 'done') return false;
  const gate = currentGate(rollout);
  return gate === 'kpi' || gate === 'both';
}
